import React, { useState, useEffect, useCallback } from "react";
import { Pagination, Stack } from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import InventoryItemCard from "../components/inventoryItems/InventoryItemCard";
import CreateInventoryItemModal from "../components/inventoryItems/CreateInventoryItemModal";
import UpdateInventoryItemModal from "../components/inventoryItems/UpdateInventoryItemModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getInventoryItems,
  deleteInventoryItem,
  resetInventoryItemServerStatus,
} from "../redux/actions/inventoryItemActions";
import { InventoryItemResponseDto } from "../shared/dtos/inventoryItem/InventoryItemResponseDto";

const InventoryItemsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: inventoryItems,
    pagination,
    loadingFetchAll: loadingInventoryItems,
    errorFetchAll: errorLoadingInventoryItems,
    serverStatus,
    loadingCreate: loadingInventoryCreate,
    errorCreate: errorCreatingInventory,
    loadingUpdate: loadingInventoryUpdate,
    errorUpdate: errorUpdatingInventory,
    loadingDelete: loadingInventoryDelete,
  } = useAppSelector((state) => state.inventoryItem);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 12 });

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] =
    useState<InventoryItemResponseDto | null>(null);

  // Permissions
  const canCreate = hasPermission("inventory_item:create");
  const canUpdate = hasPermission("inventory_item:update");
  const canDelete = hasPermission("inventory_item:delete");

  const fetchInventoryList = useCallback((): void => {
    dispatch(
      getInventoryItems({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch.trim() || undefined,
      }),
    );
  }, [dispatch, pageConfig.page, pageConfig.limit, debouncedSearch]);

  useEffect(() => {
    fetchInventoryList();
  }, [fetchInventoryList]);

  // Modal Handlers
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingInventoryCreate) setIsCreateModalOpen(false);
  }, [loadingInventoryCreate]);

  const handleOpenUpdate = (item: InventoryItemResponseDto): void => {
    setSelectedItem(item);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingInventoryUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedItem(null);
    }
  }, [loadingInventoryUpdate]);

  const handleOpenDelete = (item: InventoryItemResponseDto): void => {
    setSelectedItem(item);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingInventoryDelete) {
      setIsDeleteModalOpen(false);
      setSelectedItem(null);
    }
  }, [loadingInventoryDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedItem) {
      dispatch(deleteInventoryItem(selectedItem.id));
    }
  };

  // Auto-close on server status success
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetInventoryItemServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="Inventory Management"
      subtitle="Track stock levels, costs, and supplier allocations."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddBoxIcon />}
            onClick={handleOpenCreate}
          >
            Add Item
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPageConfig((prev) => ({ ...prev, page: 1 }));
        }}
        searchPlaceholder="Search items by name, code or category..."
      />

      <CardGridContainer
        loading={loadingInventoryItems}
        error={errorLoadingInventoryItems}
        itemCount={inventoryItems?.length || 0}
        emptyMessage="No Inventory Items Found"
        onRetry={fetchInventoryList}
      >
        {inventoryItems.map((item) => (
          <InventoryItemCard
            key={item.id}
            inventory={item}
            onEdit={canUpdate ? () => handleOpenUpdate(item) : undefined}
            onDelete={canDelete ? () => handleOpenDelete(item) : undefined}
          />
        ))}
      </CardGridContainer>

      {/* Pagination Controls */}
      {pagination?.totalPages && pagination.totalPages > 1 && (
        <Stack sx={{ alignItems: "center", mt: 4 }}>
          <Pagination
            count={pagination.totalPages}
            page={pageConfig.page}
            onChange={(_, value) =>
              setPageConfig((prev) => ({ ...prev, page: value }))
            }
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateInventoryItemModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingInventoryCreate}
          error={errorCreatingInventory}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && selectedItem && (
        <UpdateInventoryItemModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          inventory={selectedItem}
          loading={loadingInventoryUpdate}
          error={errorUpdatingInventory}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteModalOpen && selectedItem && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Inventory Item"
          description={`Are you sure you want to delete "${selectedItem.name}"? This action cannot be undone.`}
          loading={loadingInventoryDelete}
        />
      )}
    </PageContainer>
  );
};

export default InventoryItemsPage;
